import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface StepperStep {
  key: string;
  label: string;
  /** A word on what the step involves, or what was recorded when it was done. */
  hint?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Key of the step being worked on. Steps before it read as complete. */
  currentKey: string;
  className?: string;
}

/**
 * The journey through onboarding, shown while it happens.
 *
 * Accepting an invitation, giving details and agreeing to terms were three
 * unconnected screens, so an invitee could not tell how much was left. Showing
 * the whole sequence answers that before it is asked.
 *
 * Rendered as an ordered list: the steps have a real order, and that order is
 * information a screen reader should get too.
 */
export function Stepper({ steps, currentKey, className }: StepperProps) {
  const currentIndex = steps.findIndex((step) => step.key === currentKey);

  return (
    <ol className={cn('space-y-1', className)}>
      {steps.map((step, index) => {
        const done = currentIndex > -1 && index < currentIndex;
        const current = index === currentIndex;

        return (
          <li
            key={step.key}
            data-testid={`step-${step.key}`}
            data-state={done ? 'complete' : current ? 'current' : 'upcoming'}
            aria-current={current ? 'step' : undefined}
            className="flex items-start gap-3 py-2"
          >
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                done && 'border-emerald-600 bg-emerald-600 text-white',
                current && 'border-primary text-primary',
                !done && !current && 'border-border text-muted-foreground',
              )}
            >
              {done ? <Check className="h-3 w-3" aria-hidden /> : index + 1}
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  'block text-sm font-medium',
                  current ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
              {step.hint && (
                <span className="block text-xs text-muted-foreground">{step.hint}</span>
              )}
              {/* Announced rather than drawn — the tick and the colour say this
                  visually, but neither reaches a screen reader. */}
              {done && <span className="sr-only">Completed</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
